import { NetworkStatus, useReadQuery } from '@apollo/client';
import { startTransition } from 'react';
import { StreamQueryQuery, StreamType } from '@/__generated__/graphql.ts';
import { ConnectionStatusType } from '@/components/connection-status/connection-status-type.ts';
import { List } from '@/components/highlighted-feed-wrapper/highlighted-feed-wrapper.tsx';
import { SubscribedPostFeed } from '@/components/post-feed/subscribed-post-feed.tsx';
import { hasRightsToStream } from '@/components/streams/stream-banner/has-rights-to-stream.ts';
import {
  LockedCustomStreamCtaPost,
  LockedStreamCtaPost,
} from '@/routes/_app.p.streams_.$id._index/locked-stream-cta-post.tsx';
import { StreamSubscription } from '@/routes/_app.p.streams_.$id._index/stream-posts-subscription.ts';
import { useMe } from '@/routes/_app/use-me.ts';
import {
  AuthenticatedStreamLoaderData,
  useStreamLoaderData,
} from '../_app.p.streams_.$id/loader.ts';
import { EmptyStreamPostsFeed } from './empty-feed.tsx';
import { useStreamPostsLoaderData } from './loader.ts';
import { StreamPostsSearchFeed } from './stream-posts-search-feed.tsx';
import { StreamPreviewFeed } from './stream-preview-feed.tsx';
import { useStreamPosts } from './use-stream-posts.ts';

export const StreamPostsFeed = ({
  setSubscriptionStatus,
  skeletonItemCount,
}: {
  setSubscriptionStatus: (status: ConnectionStatusType) => void;
  skeletonItemCount?: number;
}) => {
  const { streamQueryRef } =
    useStreamLoaderData() as AuthenticatedStreamLoaderData;

  const { searchStreamPostsQueryRef } = useStreamPostsLoaderData();
  const { data } = useReadQuery(streamQueryRef);
  const stream = data?.stream;

  const me = useMe();

  if (!stream) {
    return null;
  }

  const isMe = me.username === stream.author.username;
  const accessAllowed = isMe || stream.accessAllowed;

  const isCustomStreamPostsLocked =
    stream.customTargetsData && !stream.customTargets?.length;

  return !accessAllowed || isCustomStreamPostsLocked ? (
    <LockedStreamPostsFeed stream={stream} />
  ) : searchStreamPostsQueryRef ? (
    <StreamPostsSearchFeed
      queryRef={searchStreamPostsQueryRef}
      skeletonItemCount={skeletonItemCount}
    />
  ) : (
    <UnlockedStreamPostsFeed
      streamId={stream.id}
      setSubscriptionStatus={setSubscriptionStatus}
      skeletonItemCount={skeletonItemCount}
    />
  );
};

const UnlockedStreamPostsFeed = ({
  streamId,
  setSubscriptionStatus,
  skeletonItemCount = 0,
}: {
  streamId: string;
  setSubscriptionStatus: (status: ConnectionStatusType) => void;
  skeletonItemCount?: number;
}) => {
  const {
    data: { streamPosts },
    networkStatus,
    fetchMore,
    refetch,
  } = useStreamPosts(streamId);

  const onEndReached = () => {
    if (streamPosts.nextToken) {
      startTransition(() => {
        fetchMore({
          variables: { nextToken: streamPosts.nextToken },
        });
      });
    }
  };

  return (
    <>
      <SubscribedPostFeed
        onReset={refetch}
        loading={networkStatus === NetworkStatus.loading}
        data={streamPosts.items}
        endReached={onEndReached}
        initialItemCount={skeletonItemCount}
        components={{
          List,
        }}
        gap={0}
        subscription={(onData) => (
          <StreamSubscription
            onData={onData}
            streamId={streamId}
            setSubscriptionStatus={setSubscriptionStatus}
            refetchQuery={refetch}
          />
        )}
      />

      {streamPosts.items.length === 0 ? <EmptyStreamPostsFeed /> : null}
    </>
  );
};

const LockedStreamPostsFeed = ({
  stream,
}: {
  stream: NonNullable<StreamQueryQuery['stream']>;
}) => {
  const hasRights = hasRightsToStream(stream);

  const isUnfollowedWithTargets =
    !!stream.customTargetsData &&
    !!stream.customTargets?.length &&
    hasRights &&
    !stream.accessAllowed;

  if (isUnfollowedWithTargets) {
    return (
      <LockedStreamCtaPost
        streamId={stream.id}
        inPackages={stream.inPackages}
        isPaid={!!stream.paidData?.length}
        prices={stream.paidData ?? []}
        hasRights={hasRights}
        showFollowButton
      />
    );
  }

  if (!stream.customTargetsData) {
    return stream.streamType === StreamType.Public ? (
      <StreamPreviewFeed streamId={stream.id} />
    ) : (
      <LockedStreamCtaPost
        streamId={stream.id}
        inPackages={stream.inPackages}
        isPaid={!!stream.paidData?.length}
        prices={stream.paidData ?? []}
        hasRights={hasRights}
      />
    );
  }

  return (
    <LockedCustomStreamCtaPost
      streamId={stream.id}
      customTargetsData={stream.customTargetsData}
      purchasedTargetsNumber={stream.subscription?.customTargets ?? 0}
      prices={stream.paidData ?? []}
      accessAllowed={
        stream.paidData?.length ? hasRightsToStream(stream) : stream.following
      }
      authorUsername={stream.author.username}
      subscription={stream.subscription}
    />
  );
};
